import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowDropDown, ArrowDropUp, ExpandMore } from '@mui/icons-material'
import { Box, Card, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'

import { DiscontinuedSkullIcon } from '../../../../components/DiscontinuedSkullIcon/DiscontinuedSkullIcon'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { WindowVirtualizedList } from '../../../../components/WindowVirtualizedList/WindowVirtualizedList'
import { CatalogueRowHoverCard } from '../CatalogueRowHoverCard'
import { RankBadge } from '../RankBadge'
import { adoptionChangeHover } from '../helpers/adoptionChangeCopy'
import {
  adoptionChangePp,
  buildEventCategoryPath,
  buildEventTypePath,
  catalogueChangeKind,
  catalogueChangeValue,
  catalogueMetricValue,
  CatalogueGranularity,
  CatalogueMetricKey,
  CatalogueNode,
  CatalogueTreeSortDirection,
  CatalogueTreeSortKey,
  changeColumnSupportedForLens,
  compareCatalogueNodes,
  DetailPageNavState,
  densityChangeDelta,
  eventTypeIdFromNodeId,
  formatAdoptionDelta,
  formatDensity,
  formatDensityDelta,
  formatMetricValue,
  formatSignedInt,
  buildBlueprintFamilyPath,
} from '../helpers/catalogueUtils'
import { deltaDirectionArrow } from '../helpers/deltaArrow'
import { densityChangeHover } from '../helpers/densityChangeCopy'
import { metricLensColumnDescription, metricLensLabel } from '../helpers/metricLensCopy'
import { metricValueSx } from '../helpers/typographyStyles'

// Walks the tree to see whether any node carries a change for the active lens. The adoption
// change (`deltaGames`) is fed from the momentum endpoint and the density change from the
// trends series (see enrichTree), so the Change column and its sort control self-reveal
// once the data for the lens arrives — and stay hidden (no dead all-"—" column) where it
// doesn't.
function treeHasChangeData(nodes: CatalogueNode[], metric: CatalogueMetricKey): boolean {
  return nodes.some((n) => catalogueChangeValue(n, metric) !== null || (n.children ? treeHasChangeData(n.children, metric) : false))
}

// Glossary copy for a row's hover card. Blueprints come from the glossary
// endpoint the page fetches, event types from the language map the page reads;
// event categories have no editorial description of their own.
function rowDescription(
  node: CatalogueNode,
  descriptionsByBlueprintId?: Map<string, string>,
  getEventTypeDescription?: (eventTypeId: string) => string
): string | undefined {
  if (node.kind === 'blueprint') return node.blueprintId ? descriptionsByBlueprintId?.get(node.blueprintId) : undefined
  if (node.kind === 'eventType') return getEventTypeDescription?.(eventTypeIdFromNodeId(node.id)) || undefined
  return undefined
}

interface RankedBlueprintTreeProps {
  nodes: CatalogueNode[]
  metric: CatalogueMetricKey
  // The level the top-level rows are at; names it in the metric column's tooltip.
  granularity: CatalogueGranularity
  // Expansion + column sort are fully controlled by the page (URL-backed via
  // useMarketCatalogueState) so they survive navigation and shared links.
  expandedIds: string[]
  onToggleExpand: (id: string) => void
  sortKey: CatalogueTreeSortKey
  sortDir: CatalogueTreeSortDirection
  onSortChange: (key: CatalogueTreeSortKey, dir: CatalogueTreeSortDirection) => void
  // Search string (leading "?") appended to the blueprint / event-type links a
  // row name navigates to, so the segment scope + window survive the jump.
  detailSearch?: string
  // Router state carried on every row-name link: the catalogue view to come back
  // to, plus the window the blueprint rows' momentum snapshot belongs to.
  detailNavState?: DetailPageNavState
  // Localized, unsanitized glossary descriptions per blueprint id (rendered in
  // the row's hover tooltip). Sanitized per visible row, not up front.
  descriptionsByBlueprintId?: Map<string, string>
  // Event type glossary copy, resolved on demand. A lookup rather than a map so
  // this stays presentational: the language map lives with the page.
  getEventTypeDescription?: (eventTypeId: string) => string
  // The month the Change column is measured from (`changeBaselineLabel`), for its
  // hover copy.
  changeBaselineLabel?: string
  // The period's last month (`changeLatestLabel`), the other end of the same hover copy.
  changeLatestLabel?: string
  // Left-aligned slot in the column header row — the page puts its
  // MetricLensToggle here so the control that changes the metric column sits on
  // the card whose column it changes.
  headerControls?: React.ReactNode
  isLoading?: boolean
}

type SortKey = CatalogueTreeSortKey
type SortDirection = CatalogueTreeSortDirection

const METRIC_COL_WIDTH = 108
const CHANGE_COL_WIDTH = 112
const CARET_COL_WIDTH = 24
const INDENT_PER_DEPTH = 20
const SKELETON_ROW_COUNT = 8
const SKELETON_ROW_HEIGHT = 52

function formatMetric(node: CatalogueNode, metric: CatalogueMetricKey): string {
  return formatMetricValue(metric, catalogueMetricValue(node, metric))
}

interface TreeRowProps {
  node: CatalogueNode
  depth: number
  rank: number
  // Position among the row's siblings, so assistive tech can announce "3 of 12"
  // even though virtualization keeps most siblings out of the DOM.
  setSize: number
  posInSet: number
  metric: CatalogueMetricKey
  // Row-specific booleans (computed by the parent) instead of the whole
  // expansion Set — so React.memo only re-renders the one row whose expansion
  // actually flipped, not every visible row.
  hasChildren: boolean
  isExpanded: boolean
  onToggleExpand: (id: string) => void
  showChange: boolean
  changeBaselineLabel?: string
  changeLatestLabel?: string
  detailSearch: string
  detailNavState?: DetailPageNavState
  description?: string
}

// Row chrome that is identical for every row (theme-driven but independent of
// depth/expandability). Hoisted to module scope so it isn't re-created per
// render; the depth-dependent bits live in `rowDynamicSx` below.
const treeRowStaticSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 1, sm: 1.5 },
  pr: 2,
  py: 1,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 120ms ease',
  '@media (pointer: coarse)': { minHeight: 48 },
})

const TreeRowComponent: React.FC<TreeRowProps> = ({
  node,
  depth,
  rank,
  setSize,
  posInSet,
  metric,
  hasChildren,
  isExpanded,
  onToggleExpand,
  showChange,
  changeBaselineLabel,
  changeLatestLabel,
  detailSearch,
  detailNavState,
  description,
}) => {
  const { t } = useTranslation()

  // Only the indent + depth tint change per row, so recompute this callback only
  // when depth / expandability change (theme is supplied at apply time).
  const rowDynamicSx = useMemo(
    () => (theme: Theme) => ({
      pl: `${12 + depth * INDENT_PER_DEPTH}px`,
      cursor: hasChildren ? 'pointer' : 'default',
      backgroundColor: depth > 0 ? alpha(theme.palette.text.primary, 0.015) : 'transparent',
      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
    }),
    [depth, hasChildren]
  )

  const handleActivate = useCallback(() => {
    if (hasChildren) onToggleExpand(node.id)
  }, [hasChildren, node.id, onToggleExpand])

  // The caret sits inside the clickable row; without this the row's own click
  // handler would toggle the branch a second time and cancel it out.
  const handleCaretClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      handleActivate()
    },
    [handleActivate]
  )

  const { adoptionGames, cohortSize } = node.metric
  const showAdoptionFraction = metric === 'adoption' && adoptionGames !== null && cohortSize !== null && cohortSize > 0

  // The Change column follows the metric lens: on Density it is the movement of density
  // between the period's first and last month, on every other lens the adoption change.
  const changeKind = catalogueChangeKind(metric)
  const hoverSubject = node.kind === 'eventCategory' || node.kind === 'eventType' ? node.kind : 'blueprint'

  const adoptionDelta = node.metric.deltaGames
  // Change is the adoption share's movement in percentage points over the same cohort
  // the adoption column divides by. The hover names it. Falls back to the raw signed
  // games count when the cohort is unknown/zero.
  const deltaPp = adoptionChangePp(adoptionDelta, cohortSize)

  // Shared by the Change column and the row hover card, so the two never disagree.
  const adoptionChangeValue =
    adoptionDelta === null
      ? '—'
      : deltaPp !== null
      ? formatAdoptionDelta(deltaPp)
      : t('market-analytics:catalogue_change_games', { signed: formatSignedInt(adoptionDelta) })
  // The cell shows the number alone; the definition, the games behind it and the
  // window it spans live in the hover, where they have room.
  const adoptionChangeCaption =
    adoptionDelta !== null && changeBaselineLabel
      ? t('market-analytics:catalogue_change_games_vs_month', { delta: formatSignedInt(adoptionDelta), month: changeBaselineLabel })
      : undefined

  // Density change: signed events per game, at the density column's own precision.
  const { densityChange } = node.metric
  const densityDelta = densityChangeDelta(densityChange)
  const densityChangeValue = densityDelta === null ? '—' : formatDensityDelta(densityDelta)
  const densityChangeCaption =
    densityChange && densityDelta !== null && changeBaselineLabel
      ? t('market-analytics:catalogue_density_change_vs_month', { firstValue: formatDensity(densityChange.first), month: changeBaselineLabel })
      : undefined

  const delta = changeKind === 'density' ? densityDelta : adoptionDelta
  const deltaColor = delta === null || delta === 0 ? 'text.secondary' : delta > 0 ? 'success.dark' : 'warning.dark'
  const changeValue = changeKind === 'density' ? densityChangeValue : adoptionChangeValue
  // The adoption hover is the detail pages' "Adoption change" tile copy, shared so the two
  // surfaces can't describe the same number differently (2026-09-08 review).
  const changeHover =
    changeKind === 'density'
      ? densityChangeHover({ change: densityChange, baselineLabel: changeBaselineLabel, latestLabel: changeLatestLabel }, t)
      : adoptionChangeHover({ deltaGames: adoptionDelta, baselineLabel: changeBaselineLabel, latestLabel: changeLatestLabel, subject: hoverSubject }, t)

  const changeCellContent = (
    <Typography component="span" sx={{ ...metricValueSx, color: deltaColor, lineHeight: 1.2 }} noWrap>
      {deltaDirectionArrow(delta, deltaColor)}
      {changeValue}
    </Typography>
  )

  // Subtitle ("small text") is context-aware per node kind:
  //  • Event Category → how many event types + blueprints sit under it.
  //  • Event Type → which category it belongs to + how many blueprints.
  //  • Blueprint (leaf) → its category + event type breadcrumb.
  const blueprintCountText = t('market-analytics:catalogue_tree_blueprint_count', { count: node.metric.blueprintCount })
  const leafSubtitle = [node.categoryName, node.eventTypeName].filter(Boolean).join(' · ')
  let subtitle: string | undefined
  if (node.kind === 'eventCategory') {
    subtitle = `${t('market-analytics:catalogue_tree_event_type_count', { count: node.metric.eventTypeCount })} · ${blueprintCountText}`
  } else if (node.kind === 'eventType') {
    subtitle = node.categoryName ? `${node.categoryName} · ${blueprintCountText}` : blueprintCountText
  } else {
    subtitle = leafSubtitle || undefined
  }

  // Where the name navigates: every level has its own page now (blueprint, event
  // type, event category), and every one of them reads `catalogueBackSearch` for
  // its root crumb. The row behind the name keeps expanding.
  const nameTo =
    node.kind === 'blueprint'
      ? node.blueprintId
        ? buildBlueprintFamilyPath(node.blueprintId, detailSearch)
        : undefined
      : node.kind === 'eventType'
      ? buildEventTypePath(eventTypeIdFromNodeId(node.id), detailSearch)
      : buildEventCategoryPath(node.id, detailSearch)

  // The momentum snapshot only rides along when the catalogue actually has one:
  // a `deltaGames: null` would read as a known "no change" on the detail page
  // and stop it from fetching momentum itself.
  //
  // The cohort travels with the count. Sending the count alone is worse than
  // sending nothing: the detail page treats the snapshot as complete and skips
  // its own momentum fetch, then has no divisor and falls back to a raw signed
  // games count where the catalogue row it was opened from showed a percentage.
  // Event type / category pages fetch their own grouped momentum, so they only
  // need the catalogue view to come back to.
  const nameLinkState =
    node.kind === 'blueprint' && detailNavState && node.metric.deltaGames !== null
      ? { ...detailNavState, deltaGames: node.metric.deltaGames, cohortSize }
      : detailNavState

  // Hover card for the row name: where the row sits in the taxonomy, what it is,
  // and the figures the columns show — the quick-view panel's content, on hover.
  const nameTooltipContent = (
    <CatalogueRowHoverCard
      node={node}
      description={description}
      metric={metric}
      changeValue={showChange && delta !== null ? changeValue : undefined}
      changeCaption={changeKind === 'density' ? densityChangeCaption : adoptionChangeCaption}
    />
  )

  // The row is a list item, not a control: keyboard and screen reader users expand
  // it through the caret button and open its page through the name link, so each
  // row adds at most those two tab stops. Clicking anywhere else on an expandable
  // row still toggles it for mouse users.
  return (
    <Box
      role="listitem"
      aria-level={depth + 1}
      aria-setsize={setSize}
      aria-posinset={posInSet}
      onClick={hasChildren ? handleActivate : undefined}
      sx={[treeRowStaticSx, rowDynamicSx]}
    >
      <Box sx={{ width: CARET_COL_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={handleCaretClick}
            aria-expanded={isExpanded}
            aria-label={t('market-analytics:catalogue_tree_toggle_row', { name: node.name })}
            sx={{ p: 0.25, color: 'inherit' }}
          >
            <ExpandMore
              sx={{
                fontSize: 20,
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 200ms ease-in-out',
              }}
            />
          </IconButton>
        ) : null}
      </Box>
      <RankBadge rank={rank} colorHex={node.colorHex} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {/* The card sets its own width, so the tooltip's default max-width
              would only squeeze the stat tiles into two wrapped columns. */}
          <GRTooltip content={nameTooltipContent} disableMaxWidth styles={{ minWidth: 0, display: 'block', overflow: 'hidden' }}>
            {nameTo ? (
              <Link
                component={RouterLink}
                to={nameTo}
                state={nameLinkState}
                // The row behind the name toggles expansion; opening the page
                // must not also collapse the branch on the way out.
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                variant="body2"
                underline="hover"
                sx={{ display: 'block', fontWeight: 'bold', color: 'primary.main', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {node.name}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }} noWrap>
                {node.name}
              </Typography>
            )}
          </GRTooltip>
          {node.discontinued ? (
            <GRTooltip content={t('market-analytics:discontinued_blueprint_tooltip')} styles={{ display: 'inline-flex', cursor: 'help', flexShrink: 0 }}>
              <DiscontinuedSkullIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </GRTooltip>
          ) : null}
        </Box>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ width: METRIC_COL_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          component="span"
          sx={{ fontWeight: 'bold', color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: '0.9375rem', lineHeight: 1.2 }}
        >
          {formatMetric(node, metric)}
        </Typography>
        {showAdoptionFraction ? (
          <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 'bold', color: 'primary.main' }} noWrap>
            {t('market-analytics:catalogue_adoption_games_fraction', { adopting: adoptionGames, total: cohortSize })}
          </Typography>
        ) : null}
      </Box>
      {showChange ? (
        <Box sx={{ width: CHANGE_COL_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <GRTooltip content={changeHover} styles={{ display: 'inline-flex', cursor: 'help' }}>
            {changeCellContent}
          </GRTooltip>
        </Box>
      ) : null}
    </Box>
  )
}

const TreeRow = React.memo(TreeRowComponent)
TreeRow.displayName = 'TreeRow'

// One entry per visible row: the recursive tree is flattened (respecting the
// controlled expansion set) into a linear list so the ranked list can be
// window-virtualized — only on-screen rows hit the DOM, even when the whole
// catalogue (hundreds of blueprints) is in scope. Per-node expand/collapse is
// now instant rather than animated (acceptable trade-off for the scale win).
interface FlatTreeRow {
  node: CatalogueNode
  depth: number
  rank: number
  setSize: number
  posInSet: number
  hasChildren: boolean
  isExpanded: boolean
}

function flattenVisibleNodes(
  nodes: CatalogueNode[],
  expanded: Set<string>,
  metric: CatalogueMetricKey,
  sortKey: SortKey,
  direction: SortDirection,
  depth = 0,
  out: FlatTreeRow[] = []
): FlatTreeRow[] {
  // Sort this level in place on a shallow copy — nodes keep their identity so
  // memoized rows don't churn, and we don't clone the tree on a sort-dir flip.
  const sorted = [...nodes].sort((a, b) => compareCatalogueNodes(a, b, metric, sortKey, direction))
  // The badge is the row's rank by the active metric (highest = #1), not its
  // position in the list: sorting ascending or by Change reorders the rows but
  // keeps every badge, so #1 always marks the leader.
  const byMetric = sortKey === 'metric' && direction === 'desc' ? sorted : [...nodes].sort((a, b) => compareCatalogueNodes(a, b, metric, 'metric', 'desc'))
  const rankById = new Map(byMetric.map((node, index) => [node.id, index + 1]))
  sorted.forEach((node, index) => {
    const hasChildren = Boolean(node.children && node.children.length > 0)
    const isExpanded = hasChildren && expanded.has(node.id)
    out.push({ node, depth, rank: rankById.get(node.id) ?? index + 1, setSize: sorted.length, posInSet: index + 1, hasChildren, isExpanded })
    if (isExpanded) {
      flattenVisibleNodes(node.children!, expanded, metric, sortKey, direction, depth + 1, out)
    }
  })
  return out
}

interface HeaderColProps {
  label: string
  width: number
  active: boolean
  direction: SortDirection
  onClick: () => void
  tooltip?: string
}

// A plain button whose name carries the current sort direction (the list has no
// column headers for `aria-sort` to sit on, and `aria-pressed` would announce a
// toggle rather than a sort).
const HeaderColComponent: React.FC<HeaderColProps> = ({ label, width, active, direction, onClick, tooltip }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const ariaLabel = active
    ? t(direction === 'asc' ? 'market-analytics:catalogue_sort_header_asc' : 'market-analytics:catalogue_sort_header_desc', { label })
    : label
  const button = (
    <Stack
      component="button"
      type="button"
      direction="row"
      spacing={0.25}
      alignItems="center"
      justifyContent="center"
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        width,
        flexShrink: 0,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        p: 0,
        font: 'inherit',
        color: active ? 'primary.main' : 'text.secondary',
        fontSize: '0.6875rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        '&:hover': { color: 'primary.main' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2, borderRadius: '2px' },
        '@media (pointer: coarse)': { minHeight: 44, py: 1 },
      }}
    >
      <span>{label}</span>
      {active ? (
        direction === 'asc' ? (
          <ArrowDropUp sx={{ fontSize: 16 }} />
        ) : (
          <ArrowDropDown sx={{ fontSize: 16 }} />
        )
      ) : (
        <ArrowDropUp sx={{ fontSize: 16, opacity: 0 }} />
      )}
    </Stack>
  )
  return tooltip ? (
    <GRTooltip content={tooltip} styles={{ display: 'inline-flex', flexShrink: 0 }}>
      {button}
    </GRTooltip>
  ) : (
    button
  )
}
const HeaderCol = React.memo(HeaderColComponent)
HeaderCol.displayName = 'HeaderCol'

const RankedBlueprintTreeComponent: React.FC<RankedBlueprintTreeProps> = ({
  nodes,
  metric,
  granularity,
  expandedIds,
  onToggleExpand,
  sortKey,
  sortDir,
  onSortChange,
  detailSearch = '',
  detailNavState,
  descriptionsByBlueprintId,
  getEventTypeDescription,
  changeBaselineLabel,
  changeLatestLabel,
  headerControls,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  // Set form of the controlled expansion list for O(1) membership checks.
  // Ids that don't match any rendered node (stale URL state) are inert.
  const expanded = useMemo(() => new Set(expandedIds), [expandedIds])

  // Hide the Change column + its sort until real delta data exists, so the list
  // never shows a dead all-"—" column with a sort control that reorders nothing.
  const showChange = useMemo(() => changeColumnSupportedForLens(metric) && treeHasChangeData(nodes, metric), [metric, nodes])
  const effectiveSortKey: SortKey = showChange ? sortKey : 'metric'
  // A Change sort that can't apply (this lens has no Change column, or there is no
  // momentum data) falls back to the default sort: metric, highest first. The URL
  // keeps the user's choice, so it comes back once Change is available again.
  const effectiveSortDir: SortDirection = effectiveSortKey !== sortKey ? 'desc' : sortDir

  // Flatten + sort in one walk so a sort-dir flip doesn't clone the tree.
  const visibleRows = useMemo(
    () => flattenVisibleNodes(nodes, expanded, metric, effectiveSortKey, effectiveSortDir),
    [nodes, expanded, metric, effectiveSortKey, effectiveSortDir]
  )

  const getItemKey = useCallback((row: FlatTreeRow) => row.node.id, [])
  const renderItem = useCallback(
    (row: FlatTreeRow) => (
      <TreeRow
        node={row.node}
        depth={row.depth}
        rank={row.rank}
        setSize={row.setSize}
        posInSet={row.posInSet}
        metric={metric}
        hasChildren={row.hasChildren}
        isExpanded={row.isExpanded}
        onToggleExpand={onToggleExpand}
        showChange={showChange}
        changeBaselineLabel={changeBaselineLabel}
        changeLatestLabel={changeLatestLabel}
        detailSearch={detailSearch}
        detailNavState={detailNavState}
        description={rowDescription(row.node, descriptionsByBlueprintId, getEventTypeDescription)}
      />
    ),
    [
      metric,
      onToggleExpand,
      showChange,
      changeBaselineLabel,
      changeLatestLabel,
      detailSearch,
      detailNavState,
      descriptionsByBlueprintId,
      getEventTypeDescription,
    ]
  )

  // Clicking the active header flips its direction; clicking a different header
  // switches to it and resets to descending (highest first).
  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === effectiveSortKey) {
        onSortChange(key, effectiveSortDir === 'desc' ? 'asc' : 'desc')
      } else {
        onSortChange(key, 'desc')
      }
    },
    [effectiveSortKey, effectiveSortDir, onSortChange]
  )

  // Stable per-column click handlers so the memoized HeaderCol only re-renders
  // when its own active/direction state changes, not on every parent render.
  const handleSortMetric = useCallback(() => handleSort('metric'), [handleSort])
  const handleSortChange = useCallback(() => handleSort('change'), [handleSort])

  // Short header ("Adoption" / "Event density" / …); the column tooltip defines the
  // figure as the detail pages' cards do: the period's last month, at this level.
  const metricLabel = metricLensLabel(metric, t)
  const metricTooltip = metricLensColumnDescription(metric, granularity, t)

  // Rows are `listitem`s with `aria-level`, so the nesting is announced without a
  // roving-tabindex tree widget the virtualized rows couldn't support.
  const listContainerProps = useMemo(() => ({ role: 'list', 'aria-label': t('market-analytics:catalogue_tree_aria_label') }), [t])

  const headerRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1, sm: 1.5 },
    pr: 2,
    pl: `${12 + CARET_COL_WIDTH + 8}px`,
    pt: 1,
    // The metric tabs in `headerControls` carry their underline indicator on
    // their own bottom edge, so any bottom padding here leaves it hanging above
    // this row's divider with a gap under the selected tab. Letting the tabs end
    // the row puts the indicator on the divider, as tabs are meant to sit.
    pb: headerControls ? 0 : 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: alpha(theme.palette.text.primary, 0.02),
  }

  // The lens toggle lives in this card's header, so it has to stay reachable
  // while the rows are loading or empty — otherwise switching the lens back is
  // impossible from exactly the state where you'd want to.
  const controlsOnlyHeader = headerControls ? <Box sx={headerRowSx}>{headerControls}</Box> : null

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ overflow: 'hidden' }} role="group" aria-busy="true" aria-label={t('market-analytics:catalogue_tree_aria_label')}>
        {controlsOnlyHeader}
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rectangular"
            height={SKELETON_ROW_HEIGHT}
            sx={{ borderBottom: idx === SKELETON_ROW_COUNT - 1 ? 'none' : `1px solid ${theme.palette.divider}` }}
          />
        ))}
      </Card>
    )
  }

  if (visibleRows.length === 0) {
    return (
      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        {controlsOnlyHeader}
        <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
          {t('market-analytics:ranked_list_no_results')}
        </Typography>
      </Card>
    )
  }

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Column header / sort controls */}
      <Box sx={headerRowSx}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>{headerControls}</Box>
        <HeaderCol
          label={metricLabel}
          width={METRIC_COL_WIDTH}
          active={effectiveSortKey === 'metric'}
          direction={effectiveSortDir}
          onClick={handleSortMetric}
          tooltip={metricTooltip}
        />
        {showChange ? (
          <HeaderCol
            label={t('market-analytics:catalogue_change')}
            width={CHANGE_COL_WIDTH}
            active={effectiveSortKey === 'change'}
            direction={effectiveSortDir}
            onClick={handleSortChange}
            tooltip={t(catalogueChangeKind(metric) === 'density' ? 'market-analytics:tooltip_density_change' : 'market-analytics:tooltip_column_change')}
          />
        ) : null}
      </Box>
      <WindowVirtualizedList
        items={visibleRows}
        estimateSize={SKELETON_ROW_HEIGHT}
        overscan={8}
        getItemKey={getItemKey}
        renderItem={renderItem}
        containerProps={listContainerProps}
      />
    </Card>
  )
}

export const RankedBlueprintTree = React.memo(RankedBlueprintTreeComponent)
RankedBlueprintTree.displayName = 'RankedBlueprintTree'
